'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Eye, Tag } from 'lucide-react'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getProductDetailHref } from '@/constants/hooks'
import { BrandedProduct } from '@/services/dataTypes'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import OfferModalTrigger from './OfferModalTrigger'
import DiscountTag from './DiscountTag'

interface Props {
    products: BrandedProduct[]
    domain: string
    slugType: string
    mer_slug?: string
    sectionHeading?: string
    sectionSubheading?: string
}

const BrandProductsClient = ({
    products,
    domain,
    slugType,
    mer_slug = 'stores',
    sectionHeading = 'Branded',
    sectionSubheading = 'Products'
}: Props) => {
    const [selectedBrand, setSelectedBrand] = useState<string>('All')

    if (!products || !products.length) return null

    // Extract unique merchant names for filter tabs
    const brandNames = Array.from(
        new Set(products.map(p => p.merchant?.merchant_name).filter(Boolean))
    )

    const filteredProducts = selectedBrand === 'All'
        ? products
        : products.filter(p => p.merchant?.merchant_name === selectedBrand)

    return (
        <section className="py-12 md:py-16 px-4" style={{ background: '#fffbf5' }}>
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest block mb-1" style={{ color: '#8C5826' }}>
                            Handpicked Deals
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-black text-gray-900">
                            {sectionHeading} <span style={{ color: '#8C5826' }}>{sectionSubheading}</span>
                        </h2>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Brand Filter Tabs */}
                        {brandNames.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-none">
                                <button
                                    onClick={() => setSelectedBrand('All')}
                                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border ${selectedBrand === 'All'
                                            ? 'bg-[#8C5826] text-white border-[#8C5826]'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#8C5826]'
                                        }`}
                                >
                                    All Brands
                                </button>
                                {brandNames.slice(0, 6).map((bName) => (
                                    <button
                                        key={bName}
                                        onClick={() => setSelectedBrand(bName as string)}
                                        className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border whitespace-nowrap ${selectedBrand === bName
                                                ? 'bg-[#8C5826] text-white border-[#8C5826]'
                                                : 'bg-white text-gray-700 border-gray-200 hover:border-[#8C5826]'
                                            }`}
                                    >
                                        {bName}
                                    </button>
                                ))}
                            </div>
                        )}

                        <Link
                            href="/all-products"
                            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-extrabold text-xs sm:text-sm transition-all duration-300 hover:scale-105 shadow-sm whitespace-nowrap flex-shrink-0"
                            style={{
                                background: '#F8F246',
                                color: '#8C5826',
                                border: '1px solid rgba(140,88,38,0.15)',
                                boxShadow: '0 4px 16px rgba(248, 242, 70, 0.35)',
                            }}
                        >
                            <span>View All Products</span>
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredProducts.slice(0, 8).map((prod, idx) => {
                        const offerTitle = prod.offer_title || (prod as any).title || (prod as any).offer?.offer_title || 'Featured Product'
                        const imgPath = prod.product_image || (prod as any).offer?.product_image || prod.merchant?.merchant_logo || ''
                        const imageSrc = getBaseImageUrl(domain, imgPath, '')
                        const merchant = prod.merchant
                        const merchantName = merchant?.merchant_name || 'Brand'
                        const slug = prod.slug || (prod as any).offer?.slug
                        const detailUrl = slug && merchant
                            ? getProductDetailHref(merchant, slugType, slug, prod.category?.slug)
                            : '/products'
                        const merchantHref = merchant ? getMerchantHref(merchant, mer_slug, slugType) : '/products'
                        const type = prod.offer_type?.name || 'product'
                        const isCoupon = !!prod.coupon_code

                        const origPrice = prod.original_price ? parseFloat(prod.original_price) : 0
                        const salePrice = prod.sale_price ? parseFloat(prod.sale_price) : 0
                        const discountPercent = origPrice > 0 && salePrice > 0
                            ? Math.round(((origPrice - salePrice) / origPrice) * 100)
                            : null
                        const discountTag = getFinalDiscountTag(offerTitle, discountPercent)
                        const currency = getCurrencySymbol(prod?.currency)

                        return (
                            <div
                                key={prod.id || idx}
                                className="group flex flex-col justify-between p-3.5 sm:p-4 rounded-2xl bg-white border border-[#f0ede8] transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                            >
                                <div>
                                    <div className="relative w-full h-40 rounded-xl overflow-hidden mb-3 bg-[#fffbf0] flex items-center justify-center p-3 border border-[#f0ede8]">
                                        {discountTag && (
                                            <div className="absolute top-2 left-2 z-10">
                                                <DiscountTag tag={discountTag} />
                                            </div>
                                        )}
                                        {imgPath ? (
                                            <Image
                                                src={imageSrc}
                                                alt={discardHTMLTags(offerTitle)}
                                                fill
                                                className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <Tag size={32} className="text-[#8C5826] opacity-30" />
                                        )}
                                    </div>
                                    {merchant?.merchant_name && (
                                        <span className="text-[10px] font-bold text-[#8C5826] uppercase tracking-wider block mb-1 text-center">
                                            {merchant.merchant_name}
                                        </span>
                                    )}
                                    <Link href={detailUrl} className="block">
                                        <h3 className="text-xs font-bold text-gray-900 text-center line-clamp-2 hover:text-[#8C5826] transition-colors leading-snug">
                                            {discardHTMLTags(offerTitle)}
                                        </h3>
                                    </Link>
                                </div>

                                <div className="mt-3 pt-2.5 border-t border-gray-100 flex flex-col gap-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <div>
                                            {salePrice > 0 || origPrice > 0 ? (
                                                <div className="flex items-baseline gap-1.5">
                                                    {salePrice > 0 && (
                                                        <span className="text-sm font-black text-gray-900">
                                                            {currency}{salePrice.toFixed(2)}
                                                        </span>
                                                    )}
                                                    {origPrice > 0 && origPrice > salePrice && (
                                                        <span className="text-[10px] text-gray-400 line-through">
                                                            {currency}{origPrice.toFixed(2)}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs font-bold text-[#8C5826]">Special Deal</span>
                                            )}
                                        </div>

                                        <OfferModalTrigger
                                            product={prod as any}
                                            merchantName={merchantName}
                                            merchantLogo={imageSrc}
                                            domain={domain}
                                            className="inline-flex items-center gap-1 text-xs text-[#8C5826] hover:text-[#5e3814] cursor-pointer hover:underline font-bold transition-colors"
                                        >
                                            <Eye size={12} />
                                            <span>View Details</span>
                                        </OfferModalTrigger>
                                    </div>

                                    <OfferOutUrl
                                        unique_id={prod?.unique_id}
                                        outUrl={prod?.url}
                                        merchantHref={merchantHref}
                                        domain={domain}
                                        customClass="w-full block"
                                    >
                                        <div
                                            className="w-full flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] cursor-pointer shadow-xs hover:shadow-md"
                                            style={{
                                                background: isCoupon ? '#8C5826' : '#F8F246',
                                                color: isCoupon ? '#ffffff' : '#8C5826',
                                                border: '1px solid rgba(140,88,38,0.15)',
                                                boxShadow: isCoupon ? '0 4px 12px rgba(140, 88, 38, 0.25)' : '0 4px 12px rgba(248, 242, 70, 0.35)',
                                            }}
                                        >
                                            <span>{isCoupon ? 'Show Code' : (type === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                                            <ArrowRight size={12} />
                                        </div>
                                    </OfferOutUrl>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default BrandProductsClient
